<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    disabled?: boolean;
    disabledTheme?: 'muted' | 'success';
    ariaLabel?: string;
  }>(),
  {
    disabled: false,
    disabledTheme: 'muted',
    ariaLabel: 'Use setting',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const isDisabledSuccessState = computed<boolean>(
  () => props.disabled && props.modelValue && props.disabledTheme === 'success',
);

const onChange = (e: Event): void => {
  const el = e.target as HTMLInputElement;
  const checked = el.checked;
  // Keep the native checkbox controlled while the parent confirms or saves the change.
  el.checked = props.modelValue;
  emit('update:modelValue', checked);
};

const toggleClasses = computed<string[]>(() => {
  if (props.disabled) {
    if (isDisabledSuccessState.value) {
      return ['bg-surface-green'];
    }

    return ['bg-surface-tertiary'];
  }

  return [props.modelValue ? 'bg-surface-green' : 'bg-surface-divider'];
});
</script>

<template>
  <label
    :class="[
      'group relative inline-flex w-16 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-in-out',
      'has-focus-visible:outline-focus',
      isDisabledSuccessState && '!opacity-50',
      ...toggleClasses,
    ]"
  >
    <span
      :class="[
        'h-6 w-8 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out',
        modelValue && 'translate-x-7',
      ]"
    />

    <input
      :checked="modelValue"
      :disabled="disabled"
      type="checkbox"
      class="absolute inset-0 m-0 h-full w-full appearance-none [-webkit-appearance:none] rounded-full border-0 bg-transparent cursor-pointer focus:outline-none disabled:cursor-default"
      :aria-label="ariaLabel"
      name="setting"
      @change="onChange"
    />
  </label>
</template>
